import { getGatedIdacBackendUrl } from '../utilities/env';
import { Action, ThunkAction } from '../types';
import queryString from 'query-string';

export type StepUpResendResult = { success: true } | { success: false; errorCode: string };
export type StepUpSendCodeError = 'too_many_requests' | 'unknown_error';
export type StepUpSendResult =
  | { success: true }
  | { success: false; errorCode: StepUpSendCodeError };

export const sendCodeSuccess = (mfaToken: string): Action => ({
  type: 'STEP_UP_SEND_CODE_SUCCESS',
  mfaToken,
});

export const resendCodeSuccess = (mfaToken: string): Action => ({
  type: 'STEP_UP_RESEND_CODE_SUCCESS',
  mfaToken,
});

export const updateFromSessionStorage = (mfaToken: string): Action => ({
  type: 'STEP_UP_MFA_TOKEN_LOAD_FROM_SESSION_STORAGE',
  mfaToken,
});

export const sendCode =
  (): ThunkAction<Promise<StepUpSendResult>> => async (dispatch, getState) => {
    const {
      csrfToken,
      stepUp: { token },
    } = getState();

    const queryParams = queryString.parse(window.location.search);

    const path = getGatedIdacBackendUrl('/rest/step-up/start');
    const response = await fetch(path, {
      method: 'POST',
      body: JSON.stringify({
        continue: queryParams.continue,
        application: queryParams.application,
        token: token,
      }),
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
    });

    if (response.ok) {
      const responseBody = await response.json();
      dispatch(sendCodeSuccess(responseBody.transactionToken));
      return { success: true };
    } else if (response.status === 429) {
      return { success: false, errorCode: 'too_many_requests' };
    } else {
      return { success: false, errorCode: 'unknown_error' };
    }
  };

export const resendCode =
  (): ThunkAction<Promise<StepUpResendResult>> => async (dispatch, getState) => {
    const {
      stepUp: { mfaToken },
      csrfToken,
    } = getState();

    try {
      const path = getGatedIdacBackendUrl(`/rest/mfa/resend?transactionToken=${mfaToken}`);
      const response = await fetch(path, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
      });

      if (response.ok) {
        const responseBody = await response.json();
        dispatch(resendCodeSuccess(responseBody.transactionToken));
        return { success: true };
      } else {
        return { success: false, errorCode: 'resend_failed' };
      }
    } catch (error) {
      return { success: false, errorCode: 'resend_failed' };
    }
  };

export const verifyOtpCode =
  (otpCode: string): ThunkAction =>
  async (dispatch, getState) => {
    const { stepUp, csrfToken } = getState();
    const continueUrl = new URLSearchParams(window.location.search).get('continue');

    try {
      const path = getGatedIdacBackendUrl(`/rest/mfa/verify?transactionToken=${stepUp.mfaToken}`);
      const response = await fetch(path, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify({
          otpCode,
        }),
      });

      if (response.ok) {
        const responseBody = await response.json();
        const path = getGatedIdacBackendUrl(
          `/rest/mfa/authorize?transactionToken=${responseBody.transactionToken}`
        );
        const loginAuthorizeResp = await fetch(path);

        if (loginAuthorizeResp.ok) {
          const loginAuthorizeUrl = new URL((await loginAuthorizeResp.json()).redirectUri);
          if (continueUrl !== null) {
            loginAuthorizeUrl.searchParams.append('continue', continueUrl);
          }
          return { redirectUri: loginAuthorizeUrl.toString() };
        } else {
          return { errorCode: 'invalid_otp' };
        }
      } else {
        switch (response.status) {
          case 400:
            const body = await response.json();
            if (body.context.reason === 'invalid_transaction_token') {
              return { errorCode: 'expired_otp' };
            } else {
              return { errorCode: 'invalid_otp' };
            }
          case 403:
            return { errorCode: 'invalid_otp' };
          case 429:
            return { errorCode: 'too_many_requests' };
          default:
            return { errorCode: 'unknown' };
        }
      }
    } catch (error) {
      return { errorCode: 'unknown' };
    }
  };
